import { useState, useCallback } from 'react'
import { Link, router } from '@inertiajs/react'
import {
  User,
  Mail,
  Tag,
  Phone,
  MapPin,
  Globe,
  Monitor,
  Eye,
  MessageCircle,
  MessageSquare,
  ExternalLink,
  PanelRightClose,
  StickyNote,
  Settings,
  Hash,
  Users,
  Copy,
  Check,
} from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import AgentAssignmentDropdown from '@/components/AgentAssignmentDropdown'
import AdminActionsPanel from './AdminActionsPanel'
import CollapsibleSection from './CollapsibleSection'
import CustomerJourneyCard from './CustomerJourneyCard'
import OnlineIndicator from './OnlineIndicator'
import {
  getInitials,
  getAvatarColor,
  isUserOnline,
  formatRelativeTime,
} from './utils'
import { CONVERSATION_SELECT_PARTIAL_PROPS } from './types'
import type { ConversationDetail, Agent, CompanyStatus, CustomerJourney, EndUser, RecentConversation } from './types'

interface UserDetailsPanelProps {
  conversation: ConversationDetail
  agents: Agent[]
  currentAgentId: number
  filter: string
  statusFilter: string
  onAssignAgent: (agentId: number) => void
  onUnassignAgent: () => void
  onClose: () => void
}

export default function UserDetailsPanel({
  conversation,
  agents,
  currentAgentId,
  filter,
  statusFilter,
  onAssignAgent,
  onUnassignAgent,
  onClose,
}: UserDetailsPanelProps) {
  const [showAllConversations, setShowAllConversations] = useState(false)
  const endUser = conversation.end_user
  const contactNotes = endUser.contact_notes || []

  return (
    <aside className="w-[300px] shrink-0 bg-white border-l border-gray-200 overflow-y-auto">
      {/* User Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center gap-3 mb-4">
          <div className="relative">
            <div
              className={`w-12 h-12 rounded-full flex items-center justify-center text-white text-lg font-medium ${getAvatarColor(
                endUser.name
              )}`}
            >
              {getInitials(endUser.name)}
            </div>
            {isUserOnline(endUser.last_seen_at) && <OnlineIndicator size="lg" />}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 flex items-center gap-1">
              {endUser.name}
              <Link
                href={`/contacts/${endUser.id}`}
                className="ml-1 text-gray-400 hover:text-indigo-600"
              >
                <ExternalLink className="w-3.5 h-3.5" />
              </Link>
            </h3>
            {endUser.company && (
              endUser.company_id ? (
                <Link
                  href={`/contacts/companies/${endUser.company_id}`}
                  className="text-sm text-gray-500 hover:text-indigo-600 flex items-center gap-1 min-w-0"
                >
                  <span className="truncate">{endUser.company}</span>
                  <ExternalLink className="w-3 h-3 shrink-0" />
                </Link>
              ) : (
                <p className="text-sm text-gray-500 truncate">
                  {endUser.company}
                </p>
              )
            )}
            <CompanyStatusBadges status={endUser.company_status} />
          </div>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={onClose}
                className="flex-shrink-0 p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg cursor-pointer"
              >
                <PanelRightClose className="w-4 h-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="left">Ocultar detalhes</TooltipContent>
          </Tooltip>
        </div>

        {/* Contact Info */}
        <div className="space-y-2">
          {endUser.email && (
            <CopyableField icon={Mail} value={endUser.email} />
          )}
          {endUser.phone && (
            <CopyableField
              icon={Phone}
              value={endUser.phone}
              copyValue={stripBrazilPrefix(endUser.phone)}
            />
          )}
          {endUser.location && (
            <CopyableField icon={MapPin} value={endUser.location} />
          )}
          {endUser.company_status?.establishment_id && (
            <CopyableField
              icon={Hash}
              label={`Estabelecimento: ${endUser.company_status.establishment_id}`}
              value={String(endUser.company_status.establishment_id)}
            />
          )}
          {endUser.company_status?.user_id && (
            <CopyableField
              icon={User}
              label={`Usuário: ${endUser.company_status.user_id}`}
              value={String(endUser.company_status.user_id)}
            />
          )}
          {endUser.company_status?.patients_count != null && (
            <CopyableField
              icon={Users}
              label={`${endUser.company_status.patients_count} pacientes`}
              value={String(endUser.company_status.patients_count)}
            />
          )}
        </div>
      </div>

      {/* Tags - moved up for quick visibility */}
      {conversation.tags.length > 0 && (
        <div className="p-4 border-b border-gray-200">
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <Tag className="w-3.5 h-3.5" />
            Etiquetas
          </h4>
          <div className="flex flex-wrap gap-1.5">
            {conversation.tags.map((tag) => (
              <span
                key={tag.id}
                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                style={{
                  backgroundColor: `${tag.color}15`,
                  color: tag.color,
                }}
              >
                {tag.name}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Customer Journey */}
      <CustomerJourneyCard journey={buildCustomerJourney(endUser)} />

      {/* Admin Actions - collapsible, closed by default */}
      <AdminActionsSectionWrapper
        userActions={endUser.user_actions}
        companyActions={endUser.company_actions}
      />

      {/* Recent Conversations - collapsible, open by default */}
      {endUser.recent_conversations?.length > 0 && (
        <CollapsibleSection
          title="Conversas Recentes"
          icon={MessageSquare}
          defaultOpen
          count={endUser.conversations_count}
        >
          <div className="space-y-3">
            {(showAllConversations
              ? endUser.recent_conversations
              : endUser.recent_conversations.slice(0, 3)
            ).map((conv) => (
              <button
                key={conv.id}
                onClick={() => {
                  router.visit(`/inbox/${conv.id}?filter=${filter}&status=${statusFilter}`, {
                    preserveState: true,
                    preserveScroll: true,
                    only: CONVERSATION_SELECT_PARTIAL_PROPS,
                  })
                }}
                className={`w-full text-left p-2 rounded-lg hover:bg-gray-50 transition-colors ${
                  conv.id === conversation.id ? 'bg-orange-50 border border-orange-200' : ''
                }`}
              >
                <div className="flex items-start gap-2">
                  <ConversationAgentAvatar agent={conv.assigned_agent} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm text-gray-700">
                        Iniciado {formatRelativeTime(conv.created_at)}
                      </span>
                      <ConversationStatusBadge status={conv.status} />
                    </div>
                    <div className="flex items-center justify-between mt-0.5">
                      <span className="text-xs text-gray-500 truncate">
                        {conv.assigned_agent?.name || 'Sem atribuição'}
                      </span>
                      <span className="text-xs text-gray-400">
                        {formatRelativeTime(conv.last_message_at)}
                      </span>
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
          {endUser.recent_conversations.length > 3 && (
            <button
              onClick={() => setShowAllConversations(!showAllConversations)}
              className="mt-3 text-sm text-orange-600 hover:text-orange-700 font-medium cursor-pointer"
            >
              {showAllConversations ? 'Ver menos' : 'Ver todas'}
            </button>
          )}
        </CollapsibleSection>
      )}

      {/* Contact Notes - collapsible, closed by default */}
      <CollapsibleSection
        title="Notas do contato"
        icon={StickyNote}
        summary={contactNotes.length > 0 ? `${contactNotes.length} nota${contactNotes.length !== 1 ? 's' : ''}` : undefined}
      >
        <div className="flex items-center justify-end mb-3">
          <Link
            href={`/contacts/${endUser.id}`}
            className="text-xs text-indigo-600 hover:text-indigo-700"
          >
            Ver todas
          </Link>
        </div>
        {contactNotes.length === 0 ? (
          <p className="text-sm text-gray-500">Nenhuma nota ainda</p>
        ) : (
          <div className="space-y-2">
            {contactNotes.map((note) => (
              <div key={note.id} className="rounded-lg border border-gray-200 bg-gray-50 p-2.5">
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{note.body}</p>
                <div className="mt-1 flex items-center justify-between text-xs text-gray-500">
                  <span>{note.agent?.name || 'Sistema'}</span>
                  <span>{formatRelativeTime(note.created_at)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </CollapsibleSection>

      {/* Activity Stats - collapsible, closed by default */}
      <CollapsibleSection
        title="Atividade"
        summary={`${endUser.web_sessions_count || 0} sessões · ${endUser.messages_count || 0} msgs`}
      >
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-gray-50 rounded-lg p-2.5">
            <div className="flex items-center gap-1.5 text-gray-500 mb-1">
              <Eye className="w-3.5 h-3.5" />
              <span className="text-xs">Sessões</span>
            </div>
            <p className="text-lg font-semibold text-gray-900">
              {endUser.web_sessions_count || 0}
            </p>
          </div>
          <div className="bg-gray-50 rounded-lg p-2.5">
            <div className="flex items-center gap-1.5 text-gray-500 mb-1">
              <MessageCircle className="w-3.5 h-3.5" />
              <span className="text-xs">Mensagens</span>
            </div>
            <p className="text-lg font-semibold text-gray-900">
              {endUser.messages_count || 0}
            </p>
          </div>
        </div>
        <div className="mt-3 space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-500">Registro</span>
            <span className="text-gray-700">
              {endUser.signed_up_at ? formatRelativeTime(endUser.signed_up_at) : '—'}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Última visita</span>
            <span className="text-gray-700">
              {endUser.last_seen_at ? formatRelativeTime(endUser.last_seen_at) : '—'}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Último contato</span>
            <span className="text-gray-700">
              {endUser.last_contacted_at ? formatRelativeTime(endUser.last_contacted_at) : '—'}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Última resposta</span>
            <span className="text-gray-700">
              {endUser.last_heard_from_at ? formatRelativeTime(endUser.last_heard_from_at) : '—'}
            </span>
          </div>
        </div>
      </CollapsibleSection>

      {/* Recent Page Views - collapsible, closed by default */}
      {endUser.recent_page_views?.length > 0 && (
        <CollapsibleSection
          title="Páginas Visitadas"
          count={endUser.recent_page_views.length}
        >
          <div className="space-y-2">
            {endUser.recent_page_views.map((pv) => (
              <div key={pv.id} className="flex items-start gap-2 text-sm">
                <span className="text-gray-400 text-xs w-8 shrink-0 pt-0.5">
                  {formatRelativeTime(pv.created_at)}
                </span>
                <span className="w-1 h-1 rounded-full bg-gray-300 shrink-0 mt-2" />
                <Tooltip>
                  <TooltipTrigger asChild>
                    <a
                      href={pv.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-700 hover:text-blue-600 truncate flex-1"
                    >
                      {pv.title || new URL(pv.url).pathname}
                    </a>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs break-all">
                    {pv.url}
                  </TooltipContent>
                </Tooltip>
              </div>
            ))}
          </div>
        </CollapsibleSection>
      )}

      {/* Browser & System Info - collapsible, closed by default */}
      <SystemInfoSection
        browser={endUser.browser}
        os={endUser.os}
        browserLanguage={endUser.browser_language}
      />

      {/* Conversation Details */}
      <div className="p-4 border-b border-gray-200">
        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
          Conversa
        </h4>
        <div className="space-y-3">
          <div>
            <AgentAssignmentDropdown
              agents={agents}
              currentAgentId={currentAgentId}
              selectedAgentId={conversation.assigned_agent?.id}
              onAssign={onAssignAgent}
              onUnassign={onUnassignAgent}
              triggerVariant="inline"
            />
          </div>
          <div>
            <label className="text-xs text-gray-500">Prioridade</label>
            <p className="text-sm text-gray-700 capitalize mt-1">
              {conversation.priority}
            </p>
          </div>
          <div>
            <label className="text-xs text-gray-500">Criado em</label>
            <p className="text-sm text-gray-700 mt-1">
              {formatRelativeTime(conversation.created_at)}
            </p>
          </div>
        </div>
      </div>
    </aside>
  )
}

// -- Helpers --

function buildCustomerJourney(endUser: EndUser): CustomerJourney {
  const hasUserId = !!endUser.company_status?.user_id
  return {
    opportunity: !hasUserId,
    did_presentation: !!endUser.custom_attributes?.did_presentation,
    signed_up: hasUserId,
    paying_customer: !!endUser.company_status?.plan && endUser.company_status?.status === 'active',
    engaged: !!endUser.custom_attributes?.engaged,
  }
}

// -- Sub-components --

function ConversationAgentAvatar({ agent }: { agent: RecentConversation['assigned_agent'] }): React.ReactNode {
  if (!agent) {
    return (
      <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center shrink-0">
        <User className="w-4 h-4 text-gray-400" />
      </div>
    )
  }

  if (agent.avatar_url) {
    return (
      <img
        src={agent.avatar_url}
        alt={agent.name}
        className="w-8 h-8 rounded-full object-cover shrink-0"
      />
    )
  }

  return (
    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-medium shrink-0 ${getAvatarColor(agent.name)}`}>
      {getInitials(agent.name)}
    </div>
  )
}

// Click-to-copy field for contact info
function CopyableField({
  icon: Icon,
  value,
  label,
  copyValue,
}: {
  icon: React.ComponentType<{ className?: string }>
  value: string
  label?: string
  copyValue?: string
}): React.ReactNode {
  const [copied, setCopied] = useState(false)

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(copyValue ?? value)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }, [value, copyValue])

  return (
    <button
      onClick={handleCopy}
      className="flex items-center gap-2 text-sm w-full text-left group cursor-pointer"
    >
      <Icon className="w-4 h-4 text-gray-400 shrink-0" />
      <span className="text-gray-600 truncate flex-1">{label || value}</span>
      {copied ? (
        <Check className="w-3.5 h-3.5 text-green-500 shrink-0" />
      ) : (
        <Copy className="w-3.5 h-3.5 text-gray-300 opacity-0 group-hover:opacity-100 shrink-0 transition-opacity" />
      )}
    </button>
  )
}

function stripBrazilPrefix(phone: string): string {
  return phone.replace(/^\s*\+?55\s*/, '').trim()
}

// Wrapper to render AdminActionsPanel inside a CollapsibleSection
function AdminActionsSectionWrapper({
  userActions,
  companyActions,
}: {
  userActions?: ConversationDetail['end_user']['user_actions']
  companyActions?: ConversationDetail['end_user']['company_actions']
}): React.ReactNode {
  const hasUserActions = userActions && Object.values(userActions).some(Boolean)
  const hasCompanyActions = companyActions && Object.values(companyActions).some(Boolean)

  if (!hasUserActions && !hasCompanyActions) return null

  return (
    <CollapsibleSection title="Ações Admin" icon={Settings}>
      <AdminActionsPanel
        userActions={userActions}
        companyActions={companyActions}
        borderless
      />
    </CollapsibleSection>
  )
}

const CONVERSATION_STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  open: { label: 'Aberta', className: 'bg-green-100 text-green-700' },
  snoozed: { label: 'Adiada', className: 'bg-yellow-100 text-yellow-700' },
  closed: { label: 'Fechada', className: 'bg-gray-100 text-gray-600' },
}

function ConversationStatusBadge({ status }: { status: string }): React.ReactNode {
  const config = CONVERSATION_STATUS_CONFIG[status] ?? CONVERSATION_STATUS_CONFIG.closed
  return (
    <span className={`text-xs px-1.5 py-0.5 rounded ${config.className}`}>
      {config.label}
    </span>
  )
}

const STATUS_BADGE_CONFIG: Record<string, { label: string; className: string }> = {
  trial: { label: 'Trial', className: 'bg-yellow-100 text-yellow-700' },
  trial_expired: { label: 'Trial Expirado', className: 'bg-red-100 text-red-700' },
  read_mode: { label: 'Read Mode', className: 'bg-red-100 text-red-700' },
  active: { label: 'Ativo', className: 'bg-green-100 text-green-700' },
}

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  boleto: 'Boleto',
  credit_card: 'Cartão',
}

const PAYMENT_GATEWAY_LABELS: Record<string, string> = {
  asaas: 'ASAAS',
  stripe: 'Stripe',
}

function CompanyStatusBadges({ status }: { status?: CompanyStatus | null }): React.ReactNode {
  if (!status) return null

  const badges: Array<{ label: string; className: string }> = []

  if (status.plan) {
    badges.push({ label: status.plan, className: 'bg-indigo-100 text-indigo-700' })
  }

  if (status.status) {
    const config = STATUS_BADGE_CONFIG[status.status]
    if (config) {
      const trialSuffix = status.status === 'trial' && status.trial_days_left != null
        ? ` · ${status.trial_days_left}d`
        : ''
      badges.push({ label: `${config.label}${trialSuffix}`, className: config.className })
    }
  }

  if (status.payment_method) {
    const label = PAYMENT_METHOD_LABELS[status.payment_method]
    if (label) badges.push({ label, className: 'bg-gray-100 text-gray-600' })
  }

  if (status.payment_gateway) {
    const label = PAYMENT_GATEWAY_LABELS[status.payment_gateway]
    if (label) badges.push({ label, className: 'bg-gray-100 text-gray-600' })
  }

  if (badges.length === 0) return null

  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {badges.map((badge) => (
        <span
          key={badge.label}
          className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium leading-tight ${badge.className}`}
        >
          {badge.label}
        </span>
      ))}
    </div>
  )
}

interface SystemInfoSectionProps {
  browser: string | null
  os: string | null
  browserLanguage: string | null
}

function SystemInfoSection({ browser: rawBrowser, os, browserLanguage }: SystemInfoSectionProps): React.ReactNode {
  const browser = rawBrowser?.toLowerCase().includes('unknown') ? null : rawBrowser
  if (!browser && !os && !browserLanguage) return null

  const summaryParts: string[] = []
  if (os) summaryParts.push(os)
  if (browser) summaryParts.push(browser)
  const summary = summaryParts.join(' · ')

  return (
    <CollapsibleSection title="Sistema" summary={summary || undefined}>
      <div className="space-y-2 text-sm">
        {os && (
          <div className="flex items-center gap-2">
            <Monitor className="w-4 h-4 text-gray-400 shrink-0" />
            <span className="text-gray-600">{os}</span>
          </div>
        )}
        {browser && (
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4 text-gray-400 shrink-0" />
            <span className="text-gray-600">{browser}</span>
          </div>
        )}
        {browserLanguage && (
          <div className="flex items-center gap-2">
            <span className="w-4 h-4 text-gray-400 shrink-0 text-center text-xs font-medium">🌐</span>
            <span className="text-gray-600">{browserLanguage}</span>
          </div>
        )}
      </div>
    </CollapsibleSection>
  )
}
